class PdpBundlePicker extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._cards = Array.from(this.querySelectorAll('.pdp-bundle-picker__card-wrap'));
    this._mobileCta = this.querySelector('.pdp-bundle-picker__mobile-cta');
    this._mobileCtaLabel = this.querySelector('.pdp-bundle-picker__mobile-cta-label');
    this._mobileCtaPrice = this.querySelector('.pdp-bundle-picker__mobile-cta-price');
    this._mobileCtaCompareSel = this.querySelector('.pdp-bundle-picker__mobile-cta-compare s');

    if (!this._cards.length) return;

    this._cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('.pdp-bundle-picker__card-cta')) return;
        this._selectCard(card);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._selectCard(card);
        }
      });

      const cardCta = card.querySelector('.pdp-bundle-picker__card-cta');

      if (cardCta) {
        cardCta.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          this._selectCard(card);
          this._handleAddToCart(card, cardCta);
        });
      }
    });

    if (this._mobileCta) {
      this._mobileCta.addEventListener('click', (event) => {
        event.preventDefault();

        const selectedCard =
          this.querySelector('.pdp-bundle-picker__card-wrap.is-selected') ||
          this._cards.find((card) => card.hasAttribute('data-featured')) ||
          this._cards[0];

        if (!selectedCard) return;

        this._handleAddToCart(selectedCard, this._mobileCta);
      });
    }

    const defaultCard =
      this._cards.find((card) => card.hasAttribute('data-featured')) || this._cards[0];

    this._selectCard(defaultCard);
  }

  _selectCard(selected) {
    if (!selected) return;

    this._cards.forEach((card) => {
      card.classList.remove('is-selected');
      card.setAttribute('aria-checked', 'false');
    });

    selected.classList.add('is-selected');
    selected.setAttribute('aria-checked', 'true');

    if (this._mobileCta) {
      this._updateMobileCta(selected);
    }
  }

  _updateMobileCta(card) {
    if (!card || !this._mobileCta) return;

    const checkoutUrl = card.dataset.checkoutUrl || '#';
    const salePrice = card.dataset.salePrice || '';
    const comparePrice = card.dataset.comparePrice || '';
    const ctaText = card.dataset.ctaText || 'Add to Cart';

    this._mobileCta.href = checkoutUrl;
    this._mobileCta.setAttribute('aria-label', salePrice ? `${ctaText} — ${salePrice}` : ctaText);

    if (this._mobileCtaLabel) {
      this._mobileCtaLabel.textContent = ctaText;
    }

    if (this._mobileCtaPrice) {
      this._mobileCtaPrice.textContent = salePrice;
    }

    if (this._mobileCtaCompareSel) {
      this._mobileCtaCompareSel.textContent = comparePrice;
    }
  }

  _getCartItemFromUrl(url) {
    if (!url || url === '#') return null;

    let parsedUrl;

    try {
      parsedUrl = new URL(url, window.location.origin);
    } catch (error) {
      console.error('[pdp-bundle-picker] Invalid checkout URL:', url);
      return null;
    }

    const cartMatch = parsedUrl.pathname.match(/\/cart\/(.+)/);

    if (!cartMatch || !cartMatch[1]) {
      console.error('[pdp-bundle-picker] Checkout URL must be /cart/VARIANT_ID:QTY format:', url);
      return null;
    }

    const firstItem = cartMatch[1].split(',')[0];
    const parts = firstItem.split(':');

    const variantId = this._normalizeNumber(parts[0]);
    const quantity = this._normalizeNumber(parts[1]) || 1;

    if (!variantId) return null;

    return {
      id: variantId,
      quantity: quantity
    };
  }

  _normalizeNumber(value) {
    if (!value) return '';

    const match = String(value).trim().match(/(\d+)$/);
    return match ? Number(match[1]) : '';
  }

  _getSellingPlanId(card) {
    if (!card) return '';

    /*
      Supports both:
      data-selling-plan-id="688054321"
      data-selling-plan-id="gid://shopify/SellingPlan/688054321"

      Also supports checkout URL:
      /cart/123456789:1?selling_plan=688054321
    */
    let sellingPlanId = this._normalizeNumber(card.dataset.sellingPlanId);

    if (sellingPlanId) return sellingPlanId;

    const checkoutUrl = card.dataset.checkoutUrl || '';

    try {
      const parsedUrl = new URL(checkoutUrl, window.location.origin);
      sellingPlanId =
        this._normalizeNumber(parsedUrl.searchParams.get('selling_plan')) ||
        this._normalizeNumber(parsedUrl.searchParams.get('selling_plan_id'));
    } catch (error) {
      sellingPlanId = '';
    }

    return sellingPlanId || '';
  }

  async _handleAddToCart(card, button) {
    if (!card || !button) return;
    if (button.classList.contains('is-loading')) return;

    const checkoutUrl = card.dataset.checkoutUrl || '';
    const cartItem = this._getCartItemFromUrl(checkoutUrl);

    if (!cartItem || !cartItem.id) {
      console.error('[pdp-bundle-picker] Product/variant not found from checkout URL:', checkoutUrl);
      alert('Product variant is missing. Please check the Checkout URL.');
      return;
    }

    const sellingPlanId = this._getSellingPlanId(card);

    if (sellingPlanId) {
      cartItem.selling_plan = sellingPlanId;
    } else {
      console.warn('[pdp-bundle-picker] Selling plan ID missing. Product will be added as normal item.');
    }

    this._setLoading(button, true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [cartItem]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      document.dispatchEvent(
        new CustomEvent('cart:item:added', {
          bubbles: true,
          detail: {
            items: data.items || data,
            variantId: cartItem.id,
            quantity: cartItem.quantity,
            sellingPlanId: sellingPlanId || null
          }
        })
      );

      this._setLoading(button, false);
      this._openCartDrawer();
    } catch (error) {
      console.error('[pdp-bundle-picker] Add to cart error:', error);
      this._setLoading(button, false);
      alert(error.message || 'Something went wrong. Please try again.');
    }
  }

  _openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (typeof window.updateCartDrawer === 'function') {
      window.updateCartDrawer();
      return;
    }

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const cartIcon =
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('[href="/cart"]');

    if (cartIcon) {
      cartIcon.click();
      return;
    }

    window.location.href = '/cart';
  }

  _setLoading(button, loading) {
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');

    if (button.tagName === 'BUTTON') {
      button.disabled = loading;
    }
  }
}

if (!customElements.get('pdp-bundle-picker')) {
  customElements.define('pdp-bundle-picker', PdpBundlePicker);
}